import { useMemo, useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DateRange } from 'react-day-picker';

import { MatterType, UsageMetricsWithTokens } from '../../../../../backend/src/types/types';
import { formatTableData } from '../../../../../backend/src/utils/usageDataFormatting';
import { Icon } from '../../components/icon';
import useGetUsageDataByMatter from '@/api/queries/analyticsQueries/useGetUsageDataByMatter';
import useGetUsageDataByUser from '@/api/queries/analyticsQueries/useGetUsageDataByUser';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { TablePagination } from '@/new/components/table/table-pagination';

interface Row extends UsageMetricsWithTokens {
  id?: string;
  matterName?: string;
  matterCode?: string;
  userEmail?: string;
  userDisabled?: boolean;
  matterType?: MatterType;
  matterCreatedBy?: string;
}

const columnHelper = createColumnHelper<Row>();

const commonColumns = [
  columnHelper.accessor('pages', {
    header: () => <span>No. of Pages</span>,
  }),
  columnHelper.accessor('runs', {
    header: () => <span>No. of Runs</span>,
  }),
  columnHelper.accessor('facts', {
    header: () => <span>No. of Facts</span>,
  }),
  columnHelper.accessor('chronologies', {
    header: () => <span>No. of Chronologies</span>,
  }),
  columnHelper.accessor('threads', {
    header: () => <span>No. of Threads</span>,
  }),
  columnHelper.accessor('tokens', {
    header: () => <span>No. of Credits</span>,
  }),
];

function GroupSelector({ setTableGrouping }: { setTableGrouping: (g: 'user' | 'matter') => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center pl-1">
        <span className="iconify lucide--ellipsis-vertical"></span>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right">
        <DropdownMenuItem onClick={() => setTableGrouping('user')}>Group by users</DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTableGrouping('matter')}>Group by matters</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function UsageTable({
  filterDateRange,
  selectedMatterId,
  selectedUserId,
  organisationId,
  isViewingOtherOrg,
}: {
  filterDateRange: DateRange;
  selectedMatterId?: string;
  selectedUserId?: string;
  organisationId: string;
  isViewingOtherOrg?: boolean;
}) {
  const [tableGrouping, setTableGrouping] = useState<'matter' | 'user'>('matter');

  const userColumns = [
    columnHelper.accessor('userEmail', {
      header: () => (
        <div className="flex items-center gap-1">
          Email Address
          <GroupSelector setTableGrouping={setTableGrouping} />
        </div>
      ),
      cell: (info) => info.getValue(),
    }),
    ...commonColumns,
  ];

  const { data: usageDataByMatter, isLoading: isLoadingUsageDataByMatter } = useGetUsageDataByMatter(
    true,
    filterDateRange.from ? filterDateRange.from.toISOString() : '',
    filterDateRange.to ? filterDateRange.to.toISOString() : '',
    selectedMatterId,
    selectedUserId,
    organisationId,
  );

  const { data: usageDataByUser, isLoading: isLoadingUsageDataByUser } = useGetUsageDataByUser(
    true,
    filterDateRange.from ? filterDateRange.from.toISOString() : '',
    filterDateRange.to ? filterDateRange.to.toISOString() : '',
    selectedMatterId,
    selectedUserId,
    organisationId,
  );

  const tableData = useMemo(
    () => (tableGrouping === 'matter' ? formatTableData(usageDataByMatter) : formatTableData(usageDataByUser)),
    [tableGrouping, usageDataByMatter, usageDataByUser],
  );

  const matterColumns = useMemo(
    () => [
      columnHelper.accessor('matterName', {
        header: () => (
          <div className="flex items-center gap-1">
            Matter Name
            <GroupSelector setTableGrouping={setTableGrouping} />
          </div>
        ),
        cell: (info) => (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="line-clamp-1 max-w-54 overflow-ellipsis">
              {isViewingOtherOrg ? (info.row.original.id ?? '') : info.row.original.matterName}
            </span>

            {!isViewingOtherOrg && info.row.original.matterType === MatterType.Personal && (
              <span className="text-xs text-gray-500">{info.row.original.matterCreatedBy}</span>
            )}

            {!isViewingOtherOrg && info.row.original.matterCode && (
              <div className="rounded-md border bg-gray-100 px-1 py-0.25 text-xs whitespace-nowrap text-gray-600">
                {info.row.original.matterCode}
              </div>
            )}
          </div>
        ),
      }),
      ...commonColumns,
    ],
    [isViewingOtherOrg],
  );

  const tableColumns = useMemo(
    () => (tableGrouping === 'matter' ? matterColumns : userColumns),
    [tableGrouping, matterColumns],
  );

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: 'tokens',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
  });

  return (
    <div>
      {isLoadingUsageDataByMatter || isLoadingUsageDataByUser ? (
        <div className="flex h-60 items-center justify-center p-8">
          <Icon className="lucide--loader-circle animate-spin" />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination table={table} plural={tableGrouping === 'matter' ? 'matters' : 'users'} />
        </>
      )}
    </div>
  );
}
